/**
 * Поиск по Telegram и первый контакт.
 *
 * Отличается от поля поиска над списком чатов принципиально: то фильтрует
 * НАШИ чаты по нашей базе, а это спрашивает Telegram и может найти человека,
 * переписки с которым никогда не было. Совмещать их в одном списке нельзя —
 * результат поиска ещё не чат, и показывать его строкой inbox значит обещать
 * то, чего нет.
 *
 * Поиск запускается ТОЛЬКО явным действием. Подсказка по каждой букве уходила
 * бы на серверы Telegram, а лимиты считаются на аккаунт: набор слова из шести
 * букв стоил бы шести обращений и приближал flood-wait для всей компании.
 */

import { useState } from 'react';
import type { AnyTelegramAccountDto, TelegramChatDto } from '@tw/shared-types';
import { useSession } from '../state/session.tsx';
import type { TelegramSearchItemDto } from '../lib/api.js';
import { accountLabel } from './accountLabel.js';
import { Avatar } from './Avatar.tsx';
import { SearchIcon } from './icons.tsx';
import { cn } from '@/lib/utils';
import { chatTitle } from './chatTitle.js';

interface TelegramSearchProps {
  accounts: AnyTelegramAccountDto[];
  /** Выбранный аккаунт или null в режиме «Все аккаунты». */
  /** Аккаунт, выбранный в левой панели, — если выбран один конкретный. */
  selectedAccount: { kind: string; accountId?: string } | null;
  /** Чат заведён и готов к открытию. */
  onOpened: (chat: TelegramChatDto) => void;
  /**
   * Открыт ли поиск.
   *
   * Состояние поднято в родителя: кнопка, которая его открывает, стоит теперь
   * в строке поиска по своим чатам, а не отдельным рядом под ней. Отдельный
   * ряд занимал высоту постоянно ради действия, которым пользуются изредка.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Mode = 'search' | 'phone';

export function TelegramSearch({
  accounts,
  selectedAccount,
  onOpened,
  open,
  onOpenChange,
}: TelegramSearchProps) {
  const { api, can } = useSession();

  const [mode, setMode] = useState<Mode>('search');
  const [query, setQuery] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [items, setItems] = useState<TelegramSearchItemDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Аккаунт для поиска.
   *
   * В режиме «Все аккаунты» подставляется единственный доступный, если он
   * один. Если их несколько — требуем выбрать: искать «где-нибудь» бессмысленно,
   * потому что писать всё равно придётся от конкретного лица, и узнать об этом
   * после выбора собеседника уже поздно.
   */
  const chosenId = selectedAccount?.kind === "account" ? (selectedAccount.accountId ?? null) : null;
  const accountId = chosenId ?? (accounts.length === 1 ? accounts[0]?.id ?? null : null);
  const account = accounts.find((a) => a.id === accountId) ?? null;

  /*
   * Право то же, что на отправку: начать переписку значит написать.
   *
   * На клиенте проверка без scope — здесь его и нет: список прав приходит
   * плоским. Это UX, а не защита. Стажёр кнопки не увидит, а если соберёт
   * запрос руками, сервер ответит 403 — он проверяет право В ПРЕДЕЛАХ
   * конкретного аккаунта, чего клиент сделать не может.
   */
  const canWrite = can('telegram.message.send');

  const reset = (): void => {
    setItems(null);
    setError(null);
  };

  const search = async (): Promise<void> => {
    if (!accountId || query.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { items: found } = await api.telegramSearch({
        telegramAccountId: accountId,
        q: query.trim(),
      });
      setItems(found);
    } catch (err) {
      setItems(null);
      setError(err instanceof Error ? err.message : 'Поиск не удался');
    } finally {
      setBusy(false);
    }
  };

  const openItem = async (item: TelegramSearchItemDto): Promise<void> => {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    try {
      /*
       * Даже у известного чата идём тем же маршрутом, а не открываем локально.
       * Он идемпотентен, зато переписка гарантированно оказывается в списке:
       * чат мог быть заведён давно, уехать в архив и не попасть в текущую
       * выборку — тогда «открыть» без ответа сервера ничего бы не показало.
       */
      const chat = await api.openTelegramChat({
        telegramAccountId: accountId,
        ...(item.telegramUserId
          ? { telegramUserId: item.telegramUserId }
          : { username: item.username ?? '' }),
      });
      onOpened(chat);
      onOpenChange(false);
      reset();
      setQuery('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось открыть переписку');
    } finally {
      setBusy(false);
    }
  };

  const addContact = async (): Promise<void> => {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    try {
      const chat = await api.addTelegramContact({
        telegramAccountId: accountId,
        phone: phone.trim(),
        firstName: firstName.trim(),
      });
      onOpened(chat);
      onOpenChange(false);
      setPhone('');
      setFirstName('');
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить контакт');
    } finally {
      setBusy(false);
    }
  };

  // Закрытый поиск не рисует ничего: его кнопка живёт в строке поиска выше.
  if (!open) return null;

  return (
    <div className="flex-none border-b border-border bg-secondary/60 p-2.5 backdrop-blur-md">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          Поиск в Telegram
        </span>
        <button
          type="button"
          onClick={() => {
            onOpenChange(false);
            reset();
          }}
          className="rounded-full border-transparent bg-transparent px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-brand"
        >
          Закрыть
        </button>
      </div>

      {/* Аккаунт называется явно: писать будут от его лица, и молчать об
          этом нельзя — сообщение уйдёт живому человеку. */}
      {account ? (
        <p className="mb-2 truncate text-[11px] text-muted-foreground">
          От лица <span className="font-medium text-brand">{accountLabel(account)}</span>
        </p>
      ) : (
        <p className="mb-2 text-[11px] text-muted-foreground">
          Выберите аккаунт слева — искать «во всех сразу» нельзя: писать придётся
          от конкретного лица.
        </p>
      )}

      <div className="mb-2 flex gap-1 rounded-full bg-card p-1">
        {(
          [
            ['search', 'По @username'],
            ['phone', 'По номеру'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setMode(key);
              reset();
            }}
            className={cn(
              'flex-1 rounded-full border-transparent px-2 py-1 text-[11px] font-medium transition-colors',
              mode === key
                ? 'bg-primary text-primary-foreground'
                : 'bg-transparent text-muted-foreground hover:text-brand',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'search' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void search();
          }}
          className="flex gap-1.5"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="@username или имя"
            disabled={!accountId || busy}
            className="h-8 min-w-0 flex-1 rounded-full border border-border bg-card px-3 text-xs outline-none focus:border-ring"
          />
          <button
            type="submit"
            disabled={!accountId || busy || query.trim().length === 0}
            className="grid size-8 flex-none place-items-center rounded-full border-transparent bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-45"
            aria-label="Искать"
          >
            <SearchIcon className="size-4" />
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addContact();
          }}
          className="flex flex-col gap-1.5"
        >
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+79991234567"
            inputMode="tel"
            disabled={!accountId || busy || !canWrite}
            className="h-8 rounded-full border border-border bg-card px-3 text-xs outline-none focus:border-ring"
          />
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Имя контакта"
            disabled={!accountId || busy || !canWrite}
            className="h-8 rounded-full border border-border bg-card px-3 text-xs outline-none focus:border-ring"
          />
          {/* Побочный эффект назван словами. Контакт попадёт в записную книжку
              рабочего аккаунта, это видно в Telegram и отменяется только
              вручную — узнать об этом постфактум хуже, чем прочитать заранее. */}
          <p className="text-[11px] leading-snug text-muted-foreground">
            Контакт добавится в записную книжку аккаунта и будет виден в Telegram.
          </p>
          <button
            type="submit"
            disabled={!accountId || busy || !canWrite || phone.trim().length < 8 || firstName.trim().length === 0}
            className="rounded-full border-transparent bg-primary py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-45"
          >
            {busy ? 'Добавляем…' : 'Добавить и открыть переписку'}
          </button>
        </form>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded-lg bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
          {error}
        </p>
      )}

      {items && items.length === 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground">Telegram никого не нашёл</p>
      )}

      {items && items.length > 0 && (
        <ul className="mt-2 flex max-h-64 flex-col gap-0.5 overflow-y-auto">
          {items.map((item) => (
            <li key={item.telegramChatId}>
              <button
                type="button"
                disabled={busy || !canWrite}
                onClick={() => void openItem(item)}
                className="flex w-full items-center gap-2 rounded-md border-transparent bg-transparent p-1.5 text-left transition-colors hover:bg-accent disabled:opacity-45"
              >
                <Avatar name={item.title ?? item.username ?? '?'} seed={item.telegramChatId} size="xs" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-xs font-medium">
                    {item.title?.trim() || (item.username ? `@${item.username}` : chatTitle(null))}
                  </span>
                  {item.username && (
                    <span className="truncate text-[11px] text-muted-foreground">
                      @{item.username}
                    </span>
                  )}
                </span>
                {/* Уже заведённый чат помечается: менеджер должен понимать,
                    что открывает существующую переписку, а не начинает новую. */}
                <span className="flex-none text-[10px] uppercase tracking-wide text-muted-foreground">
                  {item.chatId ? 'открыть' : 'новый'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
